import { useState, useEffect, useCallback } from 'react';

interface UseOTPRateLimiterReturn {
    canResend: boolean;
    timeRemaining: number;
    startCooldown: () => void;
}

/**
 * Custom hook to manage OTP resend rate limiting
 * @param cooldownSeconds - Number of seconds to wait before allowing resend (default: 60)
 * @returns Object containing canResend status, timeRemaining, and startCooldown function
 */
export const useOTPRateLimiter = (cooldownSeconds: number = 60, startImmediately: boolean = false): UseOTPRateLimiterReturn => {
    const [timeRemaining, setTimeRemaining] = useState<number>(startImmediately ? cooldownSeconds : 0);
    const [canResend, setCanResend] = useState<boolean>(!startImmediately);

    useEffect(() => {
        if (timeRemaining <= 0) {
            setCanResend(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeRemaining((prev) => {
                const newTime = prev - 1;
                if (newTime <= 0) {
                    setCanResend(true);
                    return 0;
                }
                return newTime;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeRemaining]);

    const startCooldown = useCallback(() => {
        setTimeRemaining(cooldownSeconds);
        setCanResend(false);
    }, [cooldownSeconds]);

    return {
        canResend,
        timeRemaining,
        startCooldown,
    };
};



